import moment from "moment";
import queryString from "query-string";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { xml2js } from "../../json/xml2js";
import {
  cloneForm,
  compareForms,
  deleteFormSubmission,
  getCareTeams,
  getClientData,
  getConfig,
  getFieldLabel,
  getFieldValue,
  getFormsySubmissionsForClient,
  getSubmission,
  handleFormChange,
  saveForm,
  sendShareEmail,
  validateForm,
} from "../../scripts/formScriptsv2";
import {
  ArrayCheck,
  getFieldDate,
  getFieldName,
} from "../../scripts/helperScripts";
import BackIcon from "../common/BackIcon";
import ClientFormHeader from "../common/ClientFormHeader";
import Comment from "../common/Comment";
import {
  FormCheckbox,
  FormInput,
  FormRadioButton,
  FormTextArea,
  Select,
} from "../common/ControlBundle";
import FormButtonBar from "../common/FormButtonBar";
import ShareModal from "../common/ShareModal";
import Signature from "../common/Signature";
import { getDiagnosisDetails, getFacilityAddresses } from "../services/soar";
import { getSchema } from "./joi/validation";

function KY1293() {
  const location = useLocation();
  const navigate = useNavigate();
  const initialProgram = location.state?.selectedProgram;

  //Get route and query string params
  const { clientid, formid, submissionid } = useParams();
  const qs = queryString.parse(location.search);
  console.log("Params", clientid, formid, submissionid);
  console.log("QS:", qs);

  //Get logged in user info
  const safyInfo = JSON.parse(localStorage.getItem("safyInfo"));
  const [loadStatus, setLoadStatus] = useState({});
  //The complete Form Submission object with form data
  const [formsyDetails, setFormsyDetails] = useState({});
  const [formsyConfig, setFormsyConfig] = useState({});

  //The form data and previous forms
  const [data, setData] = useState({});
  const [allFormSubmissions, setAllFormSubmissions] = useState([]);
  //Touched fields
  const [touched, setTouched] = useState({});
  const [isSaveInProgress, setIsSaveInProgress] = useState(false);
  //Errors
  const [errors, setErrors] = useState({});
  //Client, Program and CareTeam Info
  const [clientData, setClientData] = useState({});
  const [careTeams, setCareTeams] = useState([]);

  //Signatures State Property
  const [signatures, setSignatures] = useState([]);
  //Show Signature Modal
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  //Comments state Property
  const [comments, setComments] = useState();
  //Show Comments Modal
  const [showComment, setShowComment] = useState(false);
  //Show Share Modal
  const [showShare, setShowShare] = useState(false);
  //Show Form Level (not field specific) Comments
  const [showFormComment, setShowFormComment] = useState(false);

  const [doCompare, setDoCompare] = useState(false);
  const [timeoutId, setTimeoutId] = useState();

  const pdfRef = useRef(true);
  const isMountedRef = useRef(true);

  //Get validation schema
  const schema = getSchema(formid);
  console.log("Schema Config: ", schema);

  const formDisabled = useMemo(() => {
    return formsyDetails.Active === "0" || formsyDetails.FinalSigned === "1";
  }, [formsyDetails]);

  const formConfig = useMemo(() => {
    return formsyConfig.FormConfiguration?.[0].config[0] || {};
  }, [formsyConfig]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      pdfRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!loadStatus.dataSet || !initialProgram?.ccx_client_programid) {
      return;
    }

    setData((d) => {
      const obj = { ...d };

      if (
        getFieldValue(obj.SelectedProgram) !==
        initialProgram.ccx_client_programid
      ) {
        obj.SelectedProgram.value = initialProgram.ccx_client_programid;
      }

      return obj;
    });

    setFormsyDetails((fd) => {
      const details = { ...fd };

      if (details.ClientProgramId !== initialProgram.ccx_client_programid) {
        details.ClientProgramId = initialProgram.ccx_client_programid;
        details.ClientProgramName = getFieldName(
          initialProgram.ccx_programunit
        );
      }

      return details;
    });
  }, [
    loadStatus.dataSet,
    initialProgram?.ccx_client_programid,
    initialProgram?.ccx_programunit,
  ]);

  //Get Client Info
  useEffect(() => {
    if (!formid || !clientid) {
      return;
    }

    const loadClientInfo = async () => {
      const response = await Promise.all([
        getFormsySubmissionsForClient(formid, clientid),
        getCareTeams(clientid),
      ]);

      if (!isMountedRef.current || !response) {
        return;
      }

      setAllFormSubmissions(response[0]);
      setCareTeams(response[1]);
    };

    loadClientInfo();

    if (submissionid !== undefined) {
      const loadSubmission = async () => {
        const response = await getSubmission(submissionid);

        if (!isMountedRef.current || !response) {
          return;
        }

        setData(response.Data);
        setFormsyDetails(response.FormsyDetails);

        if (response.Comments) {
          setComments(response.Comments);
        }

        if (response.Signatures) {
          setSignatures(response.Signatures);
        }

        setLoadStatus((ls) => {
          return { ...ls, dataSet: true };
        });
      };

      loadSubmission();
    }
  }, [formid, clientid, submissionid]);

  useEffect(() => {
    if (
      !formid ||
      !clientid ||
      !safyInfo?.user ||
      loadStatus.configLoaded ||
      loadStatus.loadingConfig
    ) {
      return;
    }

    const loadConfig = async () => {
      setLoadStatus((ls) => {
        return { ...ls, loadingConfig: true };
      });
      const response = await getConfig(formid);

      if (!isMountedRef.current || !response) {
        return;
      }

      setFormsyConfig(response);
      const clientDataResponse = await getClientData(
        clientid,
        submissionid !== undefined,
        `${response.FormName}${
          response.VersionIntId > 1 ? `v${response.VersionIntId}` : ""
        }`
      );

      if (!isMountedRef.current || !clientDataResponse) {
        return;
      }

      setClientData(clientDataResponse);

      if (!submissionid) {
        //Set Formsy Details
        const newForm = {
          SubmissionId: "",
          FormId: response.FormId,
          FormVersionId: formid,
          ContactId: clientid,
          ContactName: clientDataResponse.client.fullName,
          ClientProgramId: "",
          ClientProgramName: "",
          TertiaryObjectId: "",
          TertiaryObjectName: "",
          FormResults: {
            results: {},
          },

          Comments: "",
          Signatures: "",
          CreatedOn: "",
          CreatedById: safyInfo.user.azureId,
          CreatedByName: safyInfo.user.displayName,
          ModifiedById: safyInfo.user.azureId,
          ModifiedByName: safyInfo.user.displayName,
          FinalSigned: "0",
          FinalSignedDate: "",
          Active: "1",
        };
        setFormsyDetails(newForm);
        setData(response.FormConfiguration[0].config[0]);
        setLoadStatus((ls) => {
          return {
            ...ls,
            loadingConfig: false,
            configLoaded: true,
            dataSet: true,
          };
        });
      } else {
        setLoadStatus((ls) => {
          return { ...ls, loadingConfig: false, configLoaded: true };
        });
      }
    };

    loadConfig();
  }, [
    formid,
    clientid,
    safyInfo?.user,
    formsyConfig,
    submissionid,
    loadStatus.configLoaded,
    loadStatus.loadingConfig,
  ]);

  useEffect(() => {
    if (!submissionid || !!formsyDetails.SubmissionId) {
      return;
    }

    const obj = { ...formsyDetails };
    obj.SubmissionId = submissionid;
    setFormsyDetails(obj);
  }, [submissionid, formsyDetails]);

  const helperConfiguration = useMemo(() => {
    try {
      return JSON.parse(formsyConfig.HelperConfiguration[0]);
    } catch {
      const defaultConfig = {
        buttonBarConfig: {
          sign: {
            display: false,
            initialSignatureType: "full",
          },
          clone: {
            display: false,
          },
          delete: {
            display: false,
          },
          share: {
            display: false,
          },
          comment: {
            display: false,
          },
        },
        signatureType: "full",
        finalSignType: "Provider",
      };

      return defaultConfig;
    }
  }, [formsyConfig.HelperConfiguration]);

  const getSelectedProgramName = (dataObj, cData) => {
    if (!dataObj.SelectedProgram || !cData.programs) {
      return "";
    }

    let name = getFieldName(dataObj.SelectedProgram);
    if (name) {
      return name;
    } else {
      const selectedProgram = cData.programs.filter(
        (p) => p.ccx_client_programid === getFieldValue(dataObj.SelectedProgram)
      )[0];

      if (selectedProgram) {
        name = getFieldName(selectedProgram.ccx_programunit);
        const programDates = ` (${getFieldDate(
          selectedProgram.ccx_admitdate
        )} - ${
          selectedProgram.ccx_dischargedate
            ? getFieldDate(selectedProgram.ccx_dischargedate)
            : "no date"
        })`;
        return name + programDates;
      } else {
        return "";
      }
    }
  };

  //Form validation
  const validate = () => {
    const results = validateForm(data, schema);
    setErrors(results);
    return results;
  };

  const handleChange = ({ currentTarget: input }) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    const result = handleFormChange(input, data, schema, errors);
    setData(result.Data);
    setErrors(result.Errors);

    if (!submissionid) {
      return result.Data;
    }

    const touchedObj = { ...touched, [input.name]: true };
    const touchedFields = Object.keys(touchedObj).filter(
      (field) => touchedObj[field]
    );

    if (touchedFields.length >= 5) {
      if (isSaveInProgress) {
        const toId = setTimeout(() => {
          touchedFields.forEach((field) => (touchedObj[field] = false));
          handleSubmit(result.Data, null, null, null, true);
        }, 20000);

        setTimeoutId(toId);
      } else {
        clearTimeout(timeoutId);
        handleSubmit(result.Data, null, null, null, true);
        touchedFields.forEach((field) => (touchedObj[field] = false));
      }
    } else {
      const toId = setTimeout(() => {
        if (isSaveInProgress) {
          const toId2 = setTimeout(() => {
            touchedFields.forEach((field) => (touchedObj[field] = false));
            handleSubmit(result.Data, null, null, null, true);
          }, 20000);

          setTimeoutId(toId2);
        } else {
          touchedFields.forEach((field) => (touchedObj[field] = false));
          handleSubmit(result.Data, null, null, null, true);
        }
      }, 20000);

      setTimeoutId(toId);
    }

    setTouched(touchedObj);
    return result.Data;
  };

  ////SAVE
  const handleSubmit = async (
    dataOverride,
    formsyDetailsOverride,
    commentsOverride,
    signaturesOverride,
    autoSave = false
  ) => {
    if (isSaveInProgress) {
      return;
    }

    const dataObj = dataOverride || data;
    const formsyDetailsObj = formsyDetailsOverride || formsyDetails;
    const commentsObj =
      commentsOverride === undefined ? undefined : commentsOverride || comments;
    const signaturesObj = signaturesOverride || signatures;

    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    setTouched({});
    const errs = { ...errors };

    if (!getFieldValue(dataObj.SelectedProgram)) {
      errs.SelectedProgram = '"Selected Program" is required';
      setErrors(errs);
      return;
    }

    delete errs.SelectedProgram;
    setErrors(errs);
    setIsSaveInProgress(true);

    try {
      const submissionResponse = await saveForm(
        dataObj,
        formsyDetailsObj,
        commentsObj,
        signaturesObj,
        navigate,
        location,
        autoSave
      );

      if (!isMountedRef.current || !submissionResponse?.Id) {
        return;
      }

      const newDetails = { ...formsyDetailsObj };
      newDetails.ModifiedOn = submissionResponse.LastModifiedOn;
      newDetails.ModifiedById = safyInfo.user.azureId;
      newDetails.ModifiedByName = safyInfo.user.displayName;

      if (newDetails.SubmissionId === "") {
        newDetails.SubmissionId = submissionResponse?.Id;
      }

      setFormsyDetails(newDetails);
    } finally {
      setIsSaveInProgress(false);
    }
  };

  ////CLONE
  const handleOnClone = (event) => {
    event.preventDefault();
    cloneForm(
      data,
      formsyDetails,
      comments,
      signatures,
      navigate,
      location,
      formsyConfig
    );
    setSignatures([]);
  };

  ////DELETE
  const handleDelete = (e) => {
    e.preventDefault();
    if (submissionid) {
      deleteFormSubmission(
        data,
        formsyDetails,
        comments,
        signatures,
        navigate,
        location
      );
    } else {
      alert("The form could not be deleted because it has not been saved");
    }
  };

  ////SHARE
  const toggleShareModal = () => {
    setShowShare(!showShare);
  };

  const handleShareSend = (recipientList, shareComments) => {
    sendShareEmail(
      recipientList,
      shareComments,
      location.pathname,
      data,
      formsyDetails,
      comments,
      signatures,
      formsyConfig.FormDescription
    );
  };

  ////COMMENTS FUNCTIONALITY
  const [commentToEdit, setCommentToEdit] = useState();

  const toggleCommentModal = (editComment) => {
    if (!showComment && editComment) setCommentToEdit(editComment);
    setShowComment(!showComment);
  };

  const handleComments = ({ action, fieldName, comment, acknowledge }) => {
    if (action === "ADD") {
      setShowComment(true);
    } else if (comment !== undefined) {
      if (action === "EDIT") {
        toggleCommentModal(comment);
      } else if (action === "DELETE") {
        handleDeleteComment(comment);
      } else if (action === "ACKNOWLEDGE" && acknowledge !== undefined) {
        handleAcknowledgeComment(comment, acknowledge);
      }
    } else if (action === "GET") {
      return parsedComments.filter(
        (comment) => comment.field._text === fieldName
      );
    }
  };

  const commentFields = () => {
    let commentOptions = [];
    commentOptions.push({ _id: "EntireForm", name: "Entire Form----" });
    for (const property in data) {
      if (
        data[property].type !== "section" &&
        data[property].type !== "label" &&
        data[property].type !== "User" &&
        data[property].type !== "hidden"
      ) {
        const option = {
          _id: property,
          name:
            data[property].label !== undefined && data[property].label !== ""
              ? data[property].label
              : property,
        };
        commentOptions.push(option);
      }
    }
    return commentOptions;
  };

  const parsedComments = useMemo(() => {
    if (!comments) {
      return [];
    }

    const commentObj = xml2js(`<comments type="array">${comments}</comments>`, {
      compact: true,
      spaces: 4,
    });

    return ArrayCheck(commentObj.comments.comment);
  }, [comments]);

  const onCommentSave = (comment, commentToEdit) => {
    let formsyComments;
    if (commentToEdit) {
      const commentXml = `<comment><createdby>${
        commentToEdit.createdby._text
      }</createdby><datetime>${commentToEdit.datetime._text}</datetime><field>${
        commentToEdit.field._text
      }</field><comment>${commentToEdit.comment._text}</comment>${
        commentToEdit.acknowledgedby
          ? `<acknowledgedby>${commentToEdit.acknowledgedby._text}</acknowledgedby><acknowledgedon>${commentToEdit.acknowledgedon._text}</acknowledgedon>`
          : ""
      }</comment>`;
      console.log("CurrentCommentsXML: ", comments);
      console.log("CommentToReplace: ", commentXml);
      console.log("CommentToEdit: ", commentToEdit);
      console.log("CommentToEditXML: ", comment);

      formsyComments =
        comments !== undefined
          ? comments.replace(commentXml, comment)
          : comment;
      setComments(formsyComments);
    } else {
      formsyComments = comments !== undefined ? comments + comment : comment;
      setComments(formsyComments);
    }

    setShowComment(false);
    handleSubmit(null, null, formsyComments, null);
    setCommentToEdit(undefined);
  };

  const handleDeleteComment = (comment) => {
    const commentXml = `<comment><createdby>${
      safyInfo.user.displayName
    }</createdby><datetime>${comment.datetime._text}</datetime><field>${
      comment.field._text
    }</field><comment>${comment.comment._text}</comment>${
      comment.acknowledgedby
        ? `<acknowledgedby>${comment.acknowledgedby._text}</acknowledgedby><acknowledgedon>${comment.acknowledgedon._text}</acknowledgedon>`
        : ""
    }</comment>`;

    let formsyComments = comments.replace(commentXml, "");
    if (formsyComments === "") formsyComments = undefined;
    setComments(formsyComments);
    handleSubmit(null, null, formsyComments, null);
  };

  const toggleCommentToEdit = (comment) => {
    setCommentToEdit(comment);
    setShowComment(true);
  };

  const handleAcknowledgeComment = (comment, acknowledge) => {
    if (acknowledge) {
      const oldCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment></comment>`;
      const newCommentXML = `<comment><createdby>${
        comment.createdby._text
      }</createdby><datetime>${comment.datetime._text}</datetime><field>${
        comment.field._text
      }</field><comment>${comment.comment._text}</comment><acknowledgedby>${
        safyInfo.user.displayName
      }</acknowledgedby><acknowledgedon>${moment().format(
        "L"
      )} ${moment().format("LT")}</acknowledgedon></comment>`;
      const formsyComments = comments.replace(oldCommentXML, newCommentXML);
      setComments(formsyComments);
      handleSubmit(null, null, formsyComments, null);
    } else {
      const oldCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment><acknowledgedby>${comment.acknowledgedby._text}</acknowledgedby><acknowledgedon>${comment.acknowledgedon._text}</acknowledgedon></comment>`;
      const newCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment></comment>`;
      const formsyComments = comments.replace(oldCommentXML, newCommentXML);
      setComments(formsyComments);
      handleSubmit(null, null, formsyComments, null);
    }
  };

  const handleCommentNavigation = (fieldName) => {
    const element = document.getElementById(fieldName);
    if (element.type === "checkbox")
      scrollTo(
        document.getElementById(fieldName).parentElement.parentElement
          .parentElement.offsetTop - 50,
        () => {
          document.getElementById(fieldName).focus();
        }
      );
    else
      scrollTo(document.getElementById(fieldName).offsetTop - 50, () => {
        document.getElementById(fieldName).focus();
      });
  };

  const renderComments = () => {
    return parsedComments
      .filter(
        (comment) =>
          comment.field._text === "EntireForm" ||
          comment.field._text === "Entire Form----"
      )
      .map((comment, index) => (
        <div className="mt-2" key={`form-comment-${index}`}>
          {showFormComment && (
            <div
              className={`${
                comment.acknowledgedby ? "bg-safy3-50" : "bg-warning"
              } p-2 m-2 rounded d-print-none`}
              title={
                comment.acknowledgedby
                  ? `Acknowledged by: ${comment.acknowledgedby._text} ${comment.acknowledgedon._text}`
                  : ""
              }
            >
              <span className="h6">
                {comment.createdby._text ===
                  JSON.parse(localStorage.getItem("safyInfo")).user
                    .displayName && (
                  <>
                    <i
                      title="Delete Comment"
                      className="far fa-trash-alt text-white me-2"
                      onClick={() => {
                        confirmAlert({
                          title: "Delete Comment",
                          message:
                            "Are you sure you want to delete this comment?",
                          buttons: [
                            {
                              label: "Yes",
                              onClick: () => {
                                handleDeleteComment(comment);
                              },
                            },
                            {
                              label: "No",
                              onClick: () => {
                                /* do nothing */
                              },
                            },
                          ],
                        });
                      }}
                    ></i>
                    {!comment.acknowledgedby && (
                      <i
                        title="Edit Comment"
                        className="fas fa-pen text-white me-2"
                        onClick={(e) => {
                          toggleCommentToEdit(comment);
                        }}
                      ></i>
                    )}
                  </>
                )}
                {comment.acknowledgedby && (
                  <i
                    className="far fa-check-square text-white me-2"
                    onClick={() => {
                      if (
                        comment.acknowledgedby._text ===
                        JSON.parse(localStorage.getItem("safyInfo")).user
                          .displayName
                      )
                        handleAcknowledgeComment(comment, false);
                    }}
                  ></i>
                )}
                {!comment.acknowledgedby && (
                  <i
                    className="far fa-square text-white me-2"
                    onClick={() => {
                      handleAcknowledgeComment(comment, true);
                    }}
                    title="Acknowledge Comment"
                  ></i>
                )}
                <br />
                {`${comment.createdby._text} - ${comment.datetime._text}`}
              </span>
              <br />
              Regarding:{" "}
              {comment.field._text !== "EntireForm" &&
              comment.field._text !== "Entire Form----" ? (
                <button
                  className="btn btn-link"
                  onClick={() => {
                    setTimeout(() => {
                      handleCommentNavigation(comment.field._text);
                    }, 100);
                  }}
                >
                  {comment.field._text}
                </button>
              ) : (
                <>{comment.field._text}</>
              )}
              <br />
              {comment.comment._text}
            </div>
          )}
        </div>
      ));
  };

  function scrollTo(offset, callback) {
    const fixedOffset = offset.toFixed();
    const onScroll = function () {
      if (window.pageYOffset.toFixed() === fixedOffset) {
        window.removeEventListener("scroll", onScroll);
        callback();
      }
    };

    window.addEventListener("scroll", onScroll);
    onScroll();
    window.scrollTo({
      top: offset,
      behavior: "smooth",
    });
  }

  //SIGNATURE FUNCTIONALITY
  const toggleSignatureModal = (e) => {
    e.preventDefault();
    if (!isSaveInProgress) setShowSignatureModal(true);
  };

  const saveSignature = async (signatureData) => {
    console.log("Signature Data:", signatureData);

    let sigArray = [];
    if (Array.isArray(signatureData)) {
      sigArray = signatureData;
    } else {
      sigArray.push(signatureData);
    }
    setSignatures(sigArray);

    const formsyDetailsObj = { ...formsyDetails };

    if (formsyDetailsObj.FinalSigned !== "1" && checkFinalSigned(sigArray)) {
      console.log("Final Signed");
      formsyDetailsObj.FinalSigned = "1";

      if (
        formsyDetailsObj.FinalSignedDate === "" ||
        formsyDetailsObj.FinalSignedDate === "1900-01-01T00:00:00"
      ) {
        formsyDetailsObj.FinalSignedDate = new Date();
      }

      setFormsyDetails(formsyDetailsObj);
      setComments(undefined);
    }

    handleSubmit(
      null,
      formsyDetailsObj,
      formsyDetailsObj.FinalSigned === "1" ? undefined : null,
      sigArray
    );
  };

  const checkFinalSigned = (sigArray) => {
    const requiredSignatureTypes = helperConfiguration.finalSignType.split(",");

    const capturedSignatureTypes = sigArray.map((sig) => sig.signatureType);
    console.log("requiredSignatureTypes: ", requiredSignatureTypes);
    console.log("capturedSignatureTypes: ", capturedSignatureTypes);

    const missingSigTypes = requiredSignatureTypes.filter(
      (type) => !capturedSignatureTypes.includes(type)
    );

    return missingSigTypes.length === 0;
  };

  //COMPARE
  const compare = (field) => {
    if (doCompare)
      return compareForms(field, data, allFormSubmissions, formsyDetails);
  };

  const toggleCompare = () => {
    setDoCompare(!doCompare);
  };

  //CUSTOM
  const [addresses, setAddresses] = useState([]);

  const agencyOptions = useMemo(() => {
    return addresses
      .map((address) => {
        const facilityName = getFieldName(address.ccx_parentid);
        return { _id: facilityName, name: facilityName };
      })
      .sort((a, b) => {
        if (a.name > b.name) return 1;
        if (a.name < b.name) return -1;
        return 0;
      });
  }, [addresses]);

  const agencyInfo = useMemo(() => {
    const obj = {};

    addresses.forEach((address) => {
      const facilityName = getFieldName(address.ccx_parentid);
      obj[facilityName] = {
        street: address.ccx_line1,
        city: address.ccx_city,
        state: address.ccx_state,
        postalCode: address.ccx_postalcode,
        telephone: address.ccx_telephone1,
        fax: address.ccx_fax,
      };
    });

    return obj;
  }, [addresses]);

  useEffect(() => {
    const getFacilities = async () => {
      const response = await getFacilityAddresses("SAFY of Kentucky");

      if (!isMountedRef.current || !response) {
        return;
      }

      setAddresses(response);
    };

    getFacilities();
  }, []);

  useEffect(() => {
    if (!data.SelectedProgram?.value || !clientData.client) {
      return;
    }

    setData((d) => {
      const obj = { ...d };
      obj.SSNumber.value = clientData.client.ssn || "";
      return obj;
    });

    if (data.Diagnosis.value) {
      return;
    }

    const getDxDetails = async () => {
      const clientDx = await getDiagnosisDetails(data.SelectedProgram.value);

      if (!isMountedRef.current || !clientDx) {
        return;
      }

      setData((d) => {
        const obj = { ...d };
        obj.Diagnosis.value = clientDx
          .map((dx) => `${dx["dx.ccx_code"]} - ${dx["dx.ccx_name"]}`)
          .join("; ");
        return obj;
      });
    };

    getDxDetails();
  }, [
    data.SelectedProgram?.value,
    data.SSNumber?.value,
    data.Diagnosis?.value,
    clientData.client,
  ]);

  const handleAgencyChange = (e) => {
    const obj = { ...data };
    const agency = agencyInfo[e.currentTarget.value];
    obj.Facility.value =
      "Specialized Alternatives for Families and Youth (SAFY)";
    obj.FacilityAddress.value = agency.street;
    obj.FacilityCityStateZip.value = `${agency.city}, ${agency.state} ${agency.postalCode}`;
    obj.FacilityTelephone.value = agency.telephone;
    setData(obj);
  };

  const carePlanStatusOptions = [
    {
      _id: "New plan, requesting approval",
      name: "New plan, requesting approval",
    },
    {
      _id: "Initial six-month review, requesting re-approval",
      name: "Initial six-month review, requesting re-approval",
    },
    {
      _id: "Follow-up 6 month review, requesting re-approval",
      name: "Follow-up 6 month review, requesting re-approval",
    },
  ];

  ////UI
  return (
    <div style={{ fontFamily: "Times New Roman" }}>
      <div className="clearfix">
        <div className="float-start mb-2">
          <BackIcon />
        </div>
        <div className="float-end mb-2 d-print-none">
          <a
            href="https://safy365.sharepoint.com/:w:/s/Kentucky/EXsclM3AiChLkMyNTjKoSYQBQBCo2jc_chxJ-OqQfwNVgg"
            target="_blank"
            rel="noreferrer"
          >
            Forms Information
          </a>
        </div>
      </div>
      <div className={!submissionid ? "watermarked" : ""} ref={pdfRef}>
        <ClientFormHeader
          formTitle={formsyConfig.FormDescription}
          clientData={clientData.client}
          formsyDetails={formsyDetails}
          hideHeaderOnPrint={true}
          letterhead={
            <div className="fw-bold">
              DPP-1293 (Rev.06/2022)
              <br />
              <div className="w-100 text-center">
                DIVISION OF PROTECTION AND PERMANENCY
                <br />
                REHABILITATIVE SERVICES
                <br />
                PLAN OF CARE APPROVAL FORM
              </div>
            </div>
          }
        ></ClientFormHeader>
        <FormButtonBar
          config={
            formsyConfig.HelperConfiguration
              ? JSON.parse(formsyConfig.HelperConfiguration[0]).buttonBarConfig
              : {
                  sign: {
                    display: false,
                    initialSignatureType: "supervisor",
                  },
                  clone: {
                    display: false,
                  },
                  delete: {
                    display: false,
                  },
                  share: {
                    display: false,
                  },
                  comment: {
                    display: false,
                  },
                }
          }
          onSave={handleSubmit}
          onClone={handleOnClone}
          onDelete={handleDelete}
          onSignClicked={toggleSignatureModal}
          onComment={toggleCommentModal}
          onCompare={toggleCompare}
          onShare={toggleShareModal}
          signed={signatures.length > 0}
          finalSigned={formsyDetails.FinalSigned === "1"}
          validate={validate}
          pdfRef={pdfRef}
          filename={`${formsyConfig.FormDescription}`}
          newestVersion={
            formsyConfig.VersionIntId === formsyConfig.LatestVersionIntId
          }
          printStyle={`body {
            font-family: "Times New Roman";
            font-size: .75rem;
          }`}
        />
        <div className="py-2 border-bottom border-dark d-print-none">
          <b>Selected Client Program:</b>{" "}
          {getSelectedProgramName(data, clientData)}
        </div>
        {renderComments()}
        {parsedComments.filter(
          (comment) =>
            comment.field._text === "EntireForm" ||
            comment.field._text === "Entire Form----"
        ).length > 0 && (
          <i
            className="fas fa-comment-alt text-warning float-end mb-2 ms-2"
            onClick={() => setShowFormComment(!showFormComment)}
          ></i>
        )}
        <div className="mt-4">
          {data && Object.keys(data).length > 0 && (
            <>
              <div className="row">
                <div className="col">
                  <div className="d-flex lh-12 w-auto">
                    <div className="me-2">Child name:</div>
                    <div className="flex-fill">
                      <div className="w-100 ps-1 border-bottom border-dark">
                        {clientData &&
                          clientData.client &&
                          clientData.client.fullName}
                      </div>
                    </div>
                  </div>
                  <div className="d-flex lh-12 mt-2 w-auto">
                    <span className="me-2">Date of birth:</span>
                    <div className="flex-fill">
                      <div className="w-100 ps-1 border-bottom border-dark">
                        {clientData &&
                          clientData.client &&
                          getFieldDate(clientData.client.dob)}
                      </div>
                    </div>
                  </div>
                  <div className="d-flex lh-12 mt-2 w-auto">
                    <span className="me-2">Social Security number:</span>
                    <div className="flex-fill">
                      <div className="w-100">
                        <FormInput
                          name="SSNumber"
                          error={errors.SSNumber}
                          value={getFieldValue(data.SSNumber)}
                          onChange={handleChange}
                          identical={compare("SSNumber")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          divClassName="w-100 border-bottom border-dark"
                          printClassName="ps-1"
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col">
                  <FormRadioButton
                    name="PlanStatus"
                    label="Plan of Care Status:"
                    error={errors.PlanStatus}
                    value={getFieldValue(data.PlanStatus)}
                    options={carePlanStatusOptions}
                    onChange={handleChange}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                    optionsOnNewLine={true}
                    labelClassName="text-decoration-underline"
                    printClassName="text-decoration-underline"
                    optionsMarginClassName="me-2"
                  />
                </div>
              </div>
              <br />
              <FormTextArea
                name="PresentingProblems"
                label={
                  <div className="border-bottom border-dark lh-12">
                    Presenting problems:
                  </div>
                }
                error={errors.PresentingProblems}
                value={getFieldValue(data.PresentingProblems)}
                onChange={handleChange}
                identical={compare("PresentingProblems")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <FormTextArea
                name="Diagnosis"
                label={
                  <div
                    className="border-bottom border-dark"
                    style={{ lineHeight: "12px" }}
                  >
                    Diagnostic impression or DSM V diagnosis:
                  </div>
                }
                error={errors.Diagnosis}
                value={getFieldValue(data.Diagnosis)}
                onChange={handleChange}
                identical={compare("Diagnosis")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <FormTextArea
                name="Summary"
                label={
                  <div
                    className="border-bottom border-dark"
                    style={{ lineHeight: "12px" }}
                  >
                    Summary of rehabilitative goals/objectives:
                  </div>
                }
                error={errors.Summary}
                value={getFieldValue(data.Summary)}
                onChange={handleChange}
                identical={compare("Summary")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <span
                className="border-bottom border-dark"
                style={{ lineHeight: "12px" }}
              >
                Rehabilitative services, activities that are to be provided,
                based on needs of child
              </span>{" "}
              (check all that apply):
              <br />
              <span className="d-print-none">
                <br />
              </span>
              <div className="row">
                <div className="col-4 p-0 ps-3">
                  <FormCheckbox
                    name="TreatmentPlanning"
                    label="treatment planning/support"
                    checked={getFieldValue(data.TreatmentPlanning) === "true"}
                    onChange={handleChange}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                    divClassName="mb-3"
                  />
                </div>
                <div className="col-3 p-0">
                  <FormCheckbox
                    name="LivingSkills"
                    label="living skills development"
                    checked={getFieldValue(data.LivingSkills) === "true"}
                    onChange={handleChange}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                    divClassName="mb-3"
                  />
                </div>
                <div className="col-5 p-0">
                  <FormCheckbox
                    name="Therapy"
                    label="counseling, therapy, consultation, assessment"
                    checked={getFieldValue(data.Therapy) === "true"}
                    onChange={handleChange}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                    divClassName="mb-3"
                  />
                </div>
              </div>
              <FormTextArea
                name="ServiceSpecifics"
                label="Please specify services to be provided:"
                error={errors.ServiceSpecifics}
                value={getFieldValue(data.ServiceSpecifics)}
                onChange={handleChange}
                identical={compare("ServiceSpecifics")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <div className="row lh-12">
                <div className="col-7">
                  <div className="d-flex">
                    <span className="me-2">Need for services approved by:</span>
                    <span className="flex-fill">
                      <div className="row">
                        <div className="col border-bottom border-dark">
                          <FormInput
                            name="ApprovedBy"
                            error={errors.ApprovedBy}
                            value={getFieldValue(data.ApprovedBy)}
                            onChange={handleChange}
                            identical={compare("ApprovedBy")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                            customMarginBottom="mb-0"
                          />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col text-center">
                          Print name and title
                        </div>
                      </div>
                    </span>
                  </div>
                </div>
                <div className="col lh-12">
                  {signatures.filter((sig) => sig.signatureType === "Provider")
                    .length > 0 ? (
                    <>
                      <div className="row ms-1">
                        <div className="col border-bottom border-dark">
                          {signatures
                            .filter((sig) => sig.signatureType === "Provider")
                            .map((s) => {
                              return (
                                <div key={moment(s.datetime).unix()}>
                                  {`${s.name}${
                                    s.signatureRole ? ` ${s.signatureRole}` : ""
                                  }`}
                                </div>
                              );
                            })}
                        </div>
                      </div>
                      <div className="row ms-1">
                        <div className="col text-center">Signature</div>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="w-100 text-center border-top border-dark">
                        Signature
                      </div>
                    </>
                  )}
                </div>
              </div>
              <div className="row">
                <div className="col-6">
                  <div className="d-flex">
                    <span className="me-2 text-decoration-underline">
                      Effective date of plan:
                    </span>
                    <FormInput
                      name="EffectiveDate"
                      error={errors.EffectiveDate}
                      value={getFieldValue(data.EffectiveDate)}
                      onChange={handleChange}
                      identical={compare("EffectiveDate")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      type="date"
                    />
                    <FormCheckbox
                      name="EffectiveDateNA"
                      label="N/A"
                      checked={getFieldValue(data.EffectiveDateNA) === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                  </div>
                </div>
              </div>
              <br />
              <div className="row">
                <div className="col mb-2">EMAIL to:</div>
                {!formDisabled ? (
                  <div className="col mt-2 d-print-none">
                    <div className="d-flex">
                      <div className="me-2">Division:</div>
                      <div className="flex-fill">
                        <Select
                          name="Agency"
                          options={agencyOptions}
                          onChange={handleAgencyChange}
                        />
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
              <div className="row">
                <div className="col">
                  <div className="d-flex lh-12 w-auto">
                    <div className="me-2">
                      {getFieldLabel(formConfig.WorkerLabel)}
                    </div>
                    <div className="flex-fill">
                      <div className="w-100">
                        <FormInput
                          name="WorkerName"
                          error={errors.WorkerName}
                          value={getFieldValue(data.WorkerName)}
                          onChange={handleChange}
                          identical={compare("WorkerName")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          divClassName="w-100 border-bottom border-dark"
                          printClassName="ps-1"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="d-flex lh-12 w-auto">
                    <div className="me-2">DCBS Office:</div>
                    <div className="flex-fill">
                      <div className="w-100">
                        <FormInput
                          name="DCBSOffice"
                          error={errors.DCBSOffice}
                          value={getFieldValue(data.DCBSOffice)}
                          onChange={handleChange}
                          identical={compare("DCBSOffice")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          divClassName="w-100 border-bottom border-dark"
                          printClassName="ps-1"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="d-flex lh-12 w-auto">
                    <div className="me-2">Address:</div>
                    <div className="flex-fill">
                      <div className="w-100">
                        <FormInput
                          name="WorkerAddress"
                          error={errors.WorkerAddress}
                          value={getFieldValue(data.WorkerAddress)}
                          onChange={handleChange}
                          identical={compare("WorkerAddress")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          divClassName="w-100 border-bottom border-dark"
                          printClassName="ps-1"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="d-flex lh-12 w-auto">
                    <div className="me-2">City/State/Zip:</div>
                    <div className="flex-fill">
                      <div className="w-100">
                        <FormInput
                          name="WorkerCityStateZip"
                          error={errors.WorkerCityStateZip}
                          value={getFieldValue(data.WorkerCityStateZip)}
                          onChange={handleChange}
                          identical={compare("WorkerCityStateZip")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          divClassName="w-100 border-bottom border-dark"
                          printClassName="ps-1"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="d-flex mt-2 lh-12 w-auto">
                    <div className="me-2">Email address:</div>
                    <div className="flex-fill">
                      <div className="w-100">
                        <FormInput
                          name="WorkerEmail"
                          error={errors.WorkerEmail}
                          value={getFieldValue(data.WorkerEmail)}
                          onChange={handleChange}
                          identical={compare("WorkerEmail")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          divClassName="w-100 border-bottom border-dark"
                          printClassName="ps-1"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="d-flex mt-2 lh-12 w-auto">
                    <div className="me-2">Telephone:</div>
                    <div className="flex-fill">
                      <div className="w-100">
                        <FormInput
                          name="WorkerTelephone"
                          error={errors.WorkerTelephone}
                          value={getFieldValue(data.WorkerTelephone)}
                          onChange={handleChange}
                          identical={compare("WorkerTelephone")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          divClassName="w-100 border-bottom border-dark"
                          printClassName="ps-1"
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col">
                  <div className="d-flex lh-12 w-auto">
                    <div className="me-2">
                      {getFieldLabel(formConfig.FacilityLabel)}
                    </div>
                    <div className="flex-fill">
                      <div className="w-100">
                        <FormInput
                          name="Facility"
                          error={errors.Facility}
                          value={getFieldValue(data.Facility)}
                          options={agencyOptions}
                          onChange={handleChange}
                          identical={compare("Facility")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          divClassName="w-100 border-bottom border-dark"
                          printClassName="ps-1"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="d-flex lh-12 w-auto">
                    <div className="me-2">Address:</div>
                    <div className="flex-fill">
                      <div className="w-100">
                        <FormInput
                          name="FacilityAddress"
                          error={errors.FacilityAddress}
                          value={getFieldValue(data.FacilityAddress)}
                          onChange={handleChange}
                          identical={compare("Facility")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          divClassName="w-100 border-bottom border-dark"
                          printClassName="ps-1"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="d-flex lh-12 w-auto">
                    <div className="me-2">City/State/Zip:</div>
                    <div className="flex-fill">
                      <div className="w-100">
                        <FormInput
                          name="FacilityCityStateZip"
                          error={errors.FacilityCityStateZip}
                          value={getFieldValue(data.FacilityCityStateZip)}
                          onChange={handleChange}
                          identical={compare("FacilityCityStateZip")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          divClassName="w-100 border-bottom border-dark"
                          printClassName="ps-1"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="d-flex lh-12 w-auto">
                    <div className="me-2">Telephone:</div>
                    <div className="flex-fill">
                      <div className="w-100">
                        <FormInput
                          name="FacilityTelephone"
                          error={errors.FacilityTelephone}
                          value={getFieldValue(data.FacilityTelephone)}
                          onChange={handleChange}
                          identical={compare("FacilityTelephone")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          divClassName="w-100 border-bottom border-dark"
                          printClassName="ps-1"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="row mt-2">
                <div className="col">{getFieldLabel(formConfig.CHFSLabel)}</div>
              </div>
            </>
          )}
          <div>
            <div
              className="btn btn-primary m-2 d-print-none"
              onClick={() => {
                handleSubmit(null, null, null, null);
              }}
            >
              Save
            </div>
            <span className="d-print-none">
              <Signature
                canvasWidth="450"
                canvasHeight="200"
                modalTitle="Report Signatures"
                careTeams={careTeams}
                signatures={signatures}
                onSign={saveSignature}
                clientData={clientData.client}
                showModal={showSignatureModal}
                onHide={() => {
                  setShowSignatureModal(false);
                }}
                finalSignType={
                  formsyConfig.HelperConfiguration &&
                  JSON.parse(formsyConfig.HelperConfiguration[0]).finalSignType
                }
                initialSignatureType={
                  formsyConfig.HelperConfiguration &&
                  JSON.parse(formsyConfig.HelperConfiguration[0]).signatureType
                }
              ></Signature>
            </span>
            <Comment
              showModal={showComment}
              commentToEdit={commentToEdit}
              onHide={() => {
                setShowComment(false);
                setCommentToEdit(undefined);
              }}
              onSave={onCommentSave}
              fields={commentFields()}
            ></Comment>
            {showShare ? (
              <ShareModal
                showShare={showShare}
                onHide={() => setShowShare(false)}
                onSend={handleShareSend}
                submissionId={submissionid}
              ></ShareModal>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

export default KY1293;
